import { avatarUrl, userBannerUrl } from '@/lib/avatar'
import { useProfileBanner } from '@/lib/profileBanner'
import { cx } from '@/lib/cx'
import { effectSources, frameOf, plateUrl, themeVars, type Look } from '@/lib/decor'
import { useAuthStore } from '@/store/auth'
import type { AvatarDecoration, NameStyle } from '@/types/api'
import { Avatar } from '../Avatar'
import { ServerTag } from '../ServerTag'
import { StyledName } from '../StyledName'
import styles from './DecorationsSection.module.css'

export type { Look } from '@/lib/decor'

/** Как вас увидят другие: карточка профиля, строка в чате и в списке участников. */
export function DecorPreview({ look }: { look: Look }) {
  const user = useAuthStore((s) => s.user)
  const avatar = user ? avatarUrl(user.id, user.avatar) : undefined
  const banner = user ? userBannerUrl(user.id, user.banner) : undefined
  const bannerStyle = useProfileBanner(banner, avatar)
  if (!user) return null
  const name = user.global_name ?? user.username
  const initial = name.slice(0, 1).toUpperCase()
  const frame = frameOf(look.frame)
  return (
    <>
      <div className={styles.previewLabel}>Как увидят другие</div>
      <div className={cx(styles.card, look.theme && styles.cardThemed)} style={themeVars(look.theme)}>
        {look.effect ? <PreviewEffect id={look.effect} /> : null}
        <div className={styles.cardBanner} style={bannerStyle} />
        <div className={styles.cardAvatar}>
          <Avatar initial={initial} color="#5865F2" size={80} src={avatar} decoration={frame} className={styles.cardAvatarImage} />
        </div>
        <div className={styles.cardBody}>
          <span className={styles.cardNames}>
            <StyledName name={name} style={look.style} className={styles.cardName} />
            {look.tag ? <ServerTag tag={look.tag.tag} color={look.tag.color} /> : null}
          </span>
          <span className={styles.cardUsername}>@{user.username}</span>
          <div className={styles.cardPanel}>
            <span className={styles.cardPanelTitle}>{user.bio ? 'О себе' : 'В Кличате'}</span>
            {user.bio || `с ${new Date(user.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}`}
          </div>
        </div>
      </div>
      <div className={styles.previewLabel}>В чате</div>
      <div className={styles.message}>
        <Avatar initial={initial} color="#5865F2" size={40} src={avatar} decoration={frame} />
        <div className={styles.messageBody}>
          <span className={styles.messageHead}>
            <span className={styles.messageName}><StyledName name={name} style={look.style} /></span>
            {look.tag ? <ServerTag tag={look.tag.tag} color={look.tag.color} /> : null}
            <span className={styles.messageTime}>сегодня в 21:04</span>
          </span>
          <span className={styles.messageText}>Кто сегодня в отряд?</span>
        </div>
      </div>
      <div className={styles.previewLabel}>В списке участников</div>
      <div className={styles.memberPreview}>
        <PlateRow name={name} avatar={avatar} initial={initial} asset={look.plate} frame={frame} style={look.style} tag={look.tag} />
      </div>
    </>
  )
}

interface PlateRowProps {
  name: string
  avatar: string | undefined
  initial: string
  asset: string | null
  frame?: AvatarDecoration | null | undefined
  style?: NameStyle | null | undefined
  tag?: { tag: string; color: number } | null | undefined
}

/** Строка списка участников с табличкой — как её увидят другие. */
export function PlateRow({ name, avatar, initial, asset, frame, style, tag }: PlateRowProps) {
  return (
    <span className={styles.plateRow}>
      {asset ? <span className={styles.plateImage} style={{ backgroundImage: `url(${plateUrl(asset)})` }} /> : null}
      <Avatar initial={initial} color="#5865F2" size={24} src={avatar} decoration={frame} />
      <span className={styles.plateName}><StyledName name={name} style={style} /></span>
      {tag ? <ServerTag tag={tag.tag} color={tag.color} /> : null}
    </span>
  )
}

function PreviewEffect({ id }: { id: string }) {
  const src = effectSources(id)
  return <img className={styles.cardEffect} src={src.src} srcSet={src.srcSet} sizes="240px" alt="" aria-hidden="true" />
}
